import React, { useEffect, useState } from "react";
import { ArrowLeft, CalendarDays, Eye, FileText, Loader2, SearchX, UserRound, X } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useSubscription } from "../context/SubscriptionContext";
import { normalizeClinicId } from "../utils/clinicIdUtils";
import { buildApiUrl } from "../services/apiHelpers";

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export default function ReportsPage() {
  const navigate = useNavigate();
  const { patientId } = useParams();
  const { clinicInfo } = useSubscription();
  const clinicId = normalizeClinicId(clinicInfo?.id || null);
  const [reports, setReports] = useState([]);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [previewReport, setPreviewReport] = useState(null);
  const [previewState, setPreviewState] = useState({ progress: 8, status: "loading", objectUrl: null, error: "" });

  useEffect(() => {
    if (!clinicId || !patientId) return;
    let cancelled = false;
    const headers = { "X-Clinic-Id": String(clinicId) };
    setLoading(true);
    setError("");

    Promise.all([
      fetch(buildApiUrl(`reports/patient/${patientId}`), { credentials: "include", headers }),
      fetch(buildApiUrl(`clinic/patients/${clinicId}`), { credentials: "include", headers }),
    ])
      .then(async ([reportsResponse, patientsResponse]) => {
        if (!reportsResponse.ok) throw new Error("Unable to load patient reports");
        const reportsPayload = await reportsResponse.json();
        const patientsPayload = await patientsResponse.json();
        if (cancelled) return;
        setReports(Array.isArray(reportsPayload) ? reportsPayload : reportsPayload?.data || []);
        const patients = Array.isArray(patientsPayload) ? patientsPayload : patientsPayload?.data || [];
        setPatient(patients.find((item) => String(item.id) === String(patientId)) || null);
      })
      .catch((loadError) => {
        if (!cancelled) {
          setReports([]);
          setError(loadError.message || "Unable to load patient reports");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [clinicId, patientId]);

  useEffect(() => {
    if (!previewReport) return undefined;
    let cancelled = false;
    let progress = 8;
    const progressTimer = window.setInterval(() => {
      progress = Math.min(progress + 7, 88);
      if (!cancelled) setPreviewState((current) => ({ ...current, progress }));
    }, 180);

    setPreviewState({ progress, status: "loading", objectUrl: null, error: "" });
    fetch(buildApiUrl(`reports/${previewReport.id}/content`), {
        credentials: "include",
        headers: { "X-Clinic-Id": String(clinicId || "") },
      })
      .then(async (response) => {
        if (!response.ok) throw new Error("Unable to open report");
        return response.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl, "_blank", "noopener,noreferrer");
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
        setPreviewReport(null);
      })
      .catch((previewError) => {
        if (!cancelled) setPreviewState({ progress: 100, status: "error", objectUrl: null, error: previewError.message || "Unable to open report" });
      })
      .finally(() => window.clearInterval(progressTimer));

    return () => {
      cancelled = true;
      window.clearInterval(progressTimer);
      setPreviewState((current) => {
        if (current.objectUrl) URL.revokeObjectURL(current.objectUrl);
        return current;
      });
    };
  }, [clinicId, previewReport]);

  const viewReport = (report) => {
    setPreviewReport(report);
  };

  const closePreview = () => setPreviewReport(null);

  return (
    <div className="reports-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap');
        .reports-page { display:flex; flex-direction:column; width:100%; height:100%; min-height:0; box-sizing:border-box; overflow:hidden; color:#163b4d; font-family:'DM Sans', sans-serif; background:radial-gradient(circle at 100% 0%, rgba(13,148,136,.10), transparent 28%), linear-gradient(135deg, rgba(240,249,250,.64), rgba(255,255,255,.96) 42%); border:1px solid rgba(205,225,230,.68); border-radius:18px; padding:26px; animation:reports-enter .4s ease both; }
        @keyframes reports-enter { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        .reports-header { display:flex; justify-content:space-between; align-items:flex-start; gap:20px; margin-bottom:22px; }
        .reports-heading { display:flex; align-items:flex-start; gap:14px; }
        .reports-heading-icon { width:46px; height:46px; display:grid; place-items:center; border-radius:13px; background:#0e7777; color:#fff; box-shadow:0 10px 20px rgba(14,119,119,.2); }
        .reports-kicker { color:#0f8a88; font-size:10px; font-weight:800; letter-spacing:.12em; text-transform:uppercase; margin:2px 0 7px; }
        .reports-title { margin:0; color:#123047; font-family:'Space Grotesk', sans-serif; font-size:30px; font-weight:700; }
        .reports-subtitle { margin:7px 0 0; color:#688091; font-size:13px; }
        .reports-back { display:inline-flex; align-items:center; gap:7px; padding:10px 14px; border:1px solid #c8dbe2; border-radius:9px; background:rgba(255,255,255,.8); color:#24606b; font:inherit; font-size:12px; font-weight:700; cursor:pointer; transition:all .18s ease; }
        .reports-back:hover { border-color:#0faaa4; background:#effcfb; transform:translateY(-1px); }
        .reports-patient { display:flex; align-items:center; gap:13px; padding:16px 19px; margin-bottom:18px; border:1px solid #cfe5e6; border-radius:13px; background:linear-gradient(110deg, rgba(231,250,247,.95), rgba(255,255,255,.9) 70%); box-shadow:0 8px 24px rgba(27,70,86,.04); }
        .reports-avatar { width:43px; height:43px; display:grid; place-items:center; border-radius:12px; background:#c9efea; color:#087f7a; }
        .reports-patient-name { color:#173d4d; font-family:'Space Grotesk', sans-serif; font-size:17px; font-weight:700; }
        .reports-patient-id { margin-top:4px; color:#718692; font-size:12px; }
        .reports-scope { margin-left:auto; display:inline-flex; align-items:center; gap:6px; padding:6px 10px; border:1px solid #bfe4df; border-radius:999px; background:rgba(255,255,255,.7); color:#087f7a; font-size:11px; font-weight:700; }
        .reports-panel { display:flex; flex:1 1 0; flex-direction:column; min-width:0; min-height:0; overflow:hidden; border:1px solid #d6e5e9; border-radius:13px; background:rgba(255,255,255,.94); box-shadow:0 14px 34px rgba(27,70,86,.07); }
        .reports-panel-title { display:flex; justify-content:space-between; align-items:center; gap:12px; padding:18px 20px; border-bottom:1px solid #e6eef1; }
        .reports-panel-title h2 { margin:0; color:#173d4d; font-family:'Space Grotesk', sans-serif; font-size:17px; font-weight:700; }
        .reports-count { color:#0b817d; background:#e4f8f5; border:1px solid #c4ebe5; border-radius:999px; padding:5px 10px; font-size:11px; font-weight:800; }
        .reports-table-wrap { flex:1 1 0; min-width:0; min-height:0; max-height:none; overflow:auto; scrollbar-gutter:stable; overscroll-behavior:contain; }
        .reports-table { width:100%; min-width:650px; border-collapse:collapse; }
        .reports-table thead { position:sticky; top:0; z-index:2; }
        .reports-table th { padding:12px 20px; text-align:left; background:#f2f9fa; color:#6b8490; font-size:10px; font-weight:800; letter-spacing:.08em; text-transform:uppercase; white-space:nowrap; }
        .reports-table td { padding:15px 20px; border-top:1px solid #edf2f4; color:#4b6875; font-size:13px; vertical-align:middle; }
        .reports-table tr { transition:background .16s ease, transform .16s ease; }
        .reports-table tr:hover td { background:#f8fdfd; }
        .report-index { color:#8ca2aa; font-family:'Space Grotesk', sans-serif; font-size:12px; font-weight:600; }
        .report-file { display:flex; align-items:center; gap:11px; min-width:250px; color:#214959; font-weight:700; }
        .report-file-icon { width:34px; height:34px; display:grid; place-items:center; border-radius:9px; background:#e2f6f3; color:#0b8e88; flex-shrink:0; }
        .report-file-name { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .report-type { display:inline-block; padding:5px 9px; border:1px solid #e1ecee; border-radius:6px; background:#f5f9fa; color:#4e6874; font-size:11px; font-weight:700; }
        .report-date { display:inline-flex; align-items:center; gap:6px; color:#607b86; white-space:nowrap; }
        .report-view { display:inline-flex; align-items:center; gap:6px; border:1px solid #9adbd6; border-radius:7px; padding:8px 12px; background:#f4fffe; color:#087f7a; font:inherit; font-size:12px; font-weight:800; cursor:pointer; white-space:nowrap; transition:all .18s ease; }
        .report-view:hover { background:#0b918b; color:#fff; transform:translateY(-1px); box-shadow:0 6px 14px rgba(11,145,139,.18); }
        .reports-state { display:grid; place-items:center; min-height:230px; padding:30px; color:#79909a; text-align:center; }
        .reports-loading { min-height:290px; padding:26px 20px 30px; }
        .reports-loading-head { display:flex; align-items:center; gap:10px; color:#0b817d; font-size:12px; font-weight:800; }
        .reports-loading-head svg { margin:0; color:#0b918b; }
        .reports-loading-track { width:min(360px,80%); height:6px; margin:12px auto 22px; overflow:hidden; border-radius:99px; background:#dff2ef; }
        .reports-loading-progress { width:42%; height:100%; border-radius:inherit; background:linear-gradient(90deg,#0b918b,#61c9bd,#0b918b); animation:reports-loading-slide 1.3s ease-in-out infinite; }
        @keyframes reports-loading-slide { 0% { transform:translateX(-130%); } 100% { transform:translateX(340%); } }
        .reports-skeleton-list { display:grid; gap:10px; width:100%; }
        .reports-skeleton-row { display:grid; grid-template-columns:42px minmax(180px,1fr) 150px 160px 90px; align-items:center; gap:18px; padding:14px 20px; border:1px solid #edf4f4; border-radius:10px; background:#fbfefe; }
        .reports-skeleton { display:block; height:12px; border-radius:99px; background:linear-gradient(90deg,#edf5f5 25%,#dff1ef 50%,#edf5f5 75%); background-size:220% 100%; animation:reports-skeleton-shimmer 1.35s linear infinite; }
        .reports-skeleton.short { width:42px; }
        .reports-skeleton.medium { width:78%; }
        .reports-skeleton.date { width:104px; }
        .reports-skeleton.button { width:72px; height:30px; border-radius:7px; }
        @keyframes reports-skeleton-shimmer { to { background-position:-220% 0; } }
        .reports-state svg { margin-bottom:10px; color:#55aaa7; }
        .reports-error { margin-bottom:16px; padding:12px 15px; border:1px solid #fecaca; border-radius:8px; background:#fff5f5; color:#b42318; font-size:13px; }
        .reports-spin { animation:reports-spin .8s linear infinite; }
        @keyframes reports-spin { to { transform:rotate(360deg); } }
        .reports-preview-overlay { position:fixed; inset:0; z-index:3000; display:flex; align-items:center; justify-content:center; padding:20px; background:rgba(12, 50, 58, .38); backdrop-filter:blur(2px); }
        .reports-preview-modal { width:min(92vw, 420px); display:flex; flex-direction:column; overflow:hidden; border:1px solid #cfe5e6; border-radius:14px; background:#fff; box-shadow:0 24px 60px rgba(22,70,78,.25); }
        .reports-preview-head { display:flex; align-items:center; justify-content:space-between; gap:14px; padding:15px 19px; border-bottom:1px solid #e1eeee; background:linear-gradient(100deg,#f3fffe,#fff); }
        .reports-preview-title { display:flex; align-items:center; gap:10px; min-width:0; color:#173d4d; }
        .reports-preview-title-icon { width:34px; height:34px; display:grid; place-items:center; border-radius:9px; background:#dff5f2; color:#087f7a; flex-shrink:0; }
        .reports-preview-title strong { display:block; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-family:'Space Grotesk',sans-serif; font-size:14px; }
        .reports-preview-title small { display:block; margin-top:3px; color:#78919a; font-size:11px; }
        .reports-preview-close { width:31px; height:31px; display:grid; place-items:center; border:1px solid #cfe5e6; border-radius:8px; background:#fff; color:#4f737d; cursor:pointer; }
        .reports-preview-close:hover { background:#effcfb; color:#087f7a; }
        .reports-preview-body { display:flex; flex-direction:column; min-height:0; padding:18px; }
        .reports-preview-track { height:7px; overflow:hidden; border-radius:99px; background:#dff2ef; }
        .reports-preview-progress { height:100%; border-radius:inherit; background:linear-gradient(90deg,#0b817d,#61c9bd,#0b817d); transition:width .2s ease; }
        .reports-preview-status { display:flex; justify-content:space-between; gap:10px; margin-top:8px; color:#78919a; font-size:12px; }
        .reports-preview-status strong { color:#0b817d; }
        .reports-preview-error { padding:20px 4px 4px; color:#b42318; font-size:13px; text-align:center; }
        @media (max-width: 650px) { .reports-page { padding:18px 14px; border-radius:12px; } .reports-header { flex-direction:column; } .reports-heading-icon { width:40px; height:40px; } .reports-title { font-size:25px; } .reports-back { order:-1; } .reports-scope { display:none; } .reports-patient { padding:14px; } .reports-table-wrap { max-height:55vh; } .reports-skeleton-row { grid-template-columns:28px minmax(120px,1fr) 70px; gap:10px; padding:12px; } .reports-skeleton-row > :nth-child(3), .reports-skeleton-row > :nth-child(4) { display:none; } }
      `}</style>

      <div className="reports-header">
        <div className="reports-heading">
          <div className="reports-heading-icon"><FileText size={22} strokeWidth={1.8} /></div>
          <div>
            <div className="reports-kicker">Patient records</div>
            <h1 className="reports-title">Reports</h1>
            <p className="reports-subtitle">Clinical reports and documents for one patient</p>
          </div>
        </div>
        <button type="button" className="reports-back" onClick={() => navigate("/admin-dashboard/patients")}>
          <ArrowLeft size={15} /> Back to patients
        </button>
      </div>

      <div className="reports-patient">
        <div className="reports-avatar"><UserRound size={21} /></div>
        <div>
          <div className="reports-patient-name">{patient?.patientName || `Patient #${patientId}`}</div>
          <div className="reports-patient-id">Patient ID: {patientId}</div>
        </div>
        <span className="reports-scope"><UserRound size={13} /> Private patient record</span>
      </div>

      {error && <div className="reports-error">{error}</div>}
      <section className="reports-panel">
        <div className="reports-panel-title">
          <h2>All reports</h2>
          <span className="reports-count">{reports.length} {reports.length === 1 ? "file" : "files"}</span>
        </div>
        {loading ? (
          <div className="reports-state reports-loading" data-testid="reports-loading-state">
            <div className="reports-loading-head"><Loader2 size={18} className="reports-spin" /> Loading patient reports...</div>
            <div className="reports-loading-track" aria-hidden="true"><div className="reports-loading-progress" /></div>
            <div className="reports-skeleton-list" aria-hidden="true">
              {[1, 2, 3].map((row) => (
                <div className="reports-skeleton-row" key={row}>
                  <span className="reports-skeleton short" />
                  <span className="reports-skeleton medium" />
                  <span className="reports-skeleton" />
                  <span className="reports-skeleton date" />
                  <span className="reports-skeleton button" />
                </div>
              ))}
            </div>
          </div>
        ) : reports.length === 0 ? (
          <div className="reports-state"><div><SearchX size={30} /><div>No reports available for this patient.</div></div></div>
        ) : (
          <div className="reports-table-wrap">
            <table className="reports-table">
              <thead><tr><th>#</th><th>Report</th><th>Category</th><th>Added</th><th>Action</th></tr></thead>
              <tbody>{reports.map((report, index) => (
                <tr key={report.id}>
                  <td><span className="report-index">{String(index + 1).padStart(2, "0")}</span></td>
                  <td><div className="report-file"><span className="report-file-icon"><FileText size={16} /></span><span className="report-file-name">{report.fileName || "Clinical report"}</span></div></td>
                  <td><span className="report-type">{report.documentType || "Other document"}</span></td>
                  <td><span className="report-date"><CalendarDays size={14} /> {formatDate(report.createdAt)}</span></td>
                  <td><button type="button" className="report-view" onClick={() => viewReport(report)}><Eye size={14} /> View</button></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </section>
      {previewReport && (
        <div className="reports-preview-overlay" data-testid="reports-preview-overlay">
          <div className="reports-preview-modal" role="dialog" aria-modal="true" data-testid="reports-preview-modal">
            <div className="reports-preview-head">
              <div className="reports-preview-title">
                <span className="reports-preview-title-icon"><Eye size={16} /></span>
                <div>
                  <strong>{previewReport.fileName || "Clinical report"}</strong>
                  <small>Opening report in a new tab...</small>
                </div>
              </div>
              {previewState.status !== "loading" && <button type="button" className="reports-preview-close" title="Close preview" onClick={closePreview}><X size={16} /></button>}
            </div>
            <div className="reports-preview-body">
              <div className="reports-preview-track"><div className="reports-preview-progress" style={{ width: `${previewState.progress}%` }} /></div>
              <div className="reports-preview-status">
                <span>{previewState.error || (previewState.status === "loading" ? "Loading from patient records..." : "Preview ready")}</span>
                <strong>{previewState.progress}%</strong>
              </div>
              {previewState.status === "error" ? (
                <div className="reports-preview-error">{previewState.error || "Unable to open report"}</div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
